import { useEffect, useState, type FormEvent } from "react";
import { categoryNav, siteName } from "@/mocks/chigua";

interface HeaderProps {
  onSearch: (query: string) => void;
  onPick: (categoryId: string) => void;
}

const quickAnchors = [
  { id: "feed", label: "最新资讯" },
  { id: "hot", label: "热搜榜" },
  { id: "dive", label: "本期深扒" },
  { id: "faq", label: "常见问题" },
  { id: "about", label: "关于本站" },
];

export default function Header({ onSearch, onPick }: HeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSearch(keyword);
    setMenuOpen(false);
  };

  const jumpAnchor = (anchor: string) => {
    setMenuOpen(false);
    document.getElementById(anchor)?.scrollIntoView({ behavior: "smooth" });
  };

  const goCategory = (id: string) => {
    setMenuOpen(false);
    onPick(id);
  };

  return (
    <header>
      {/* 顶部细条 */}
      <div className="bg-foreground-950 text-background-50">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-1.5 text-xs md:px-6">
          <span className="inline-flex items-center gap-2 font-medium text-background-100">
            <i className="ri-flashlight-fill text-accent-400"></i>
            全网独家猛料 · 第一手未删减 · 持续更新中
          </span>
          <a
            href="mailto:hello@chiguatoutiao.cn"
            className="hidden items-center gap-1.5 font-medium text-background-100 transition hover:text-accent-300 sm:inline-flex"
          >
            <i className="ri-mail-send-line"></i>
            内容合作与线索投稿
          </a>
        </div>
      </div>

      {/* 品牌头条栏 */}
      <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 pb-5 pt-6 md:flex-row md:items-center md:justify-between md:px-6 md:pt-7">
        <a
          href="#top"
          className="group flex items-center gap-3"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        >
          <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-500 text-background-50 shadow-sm">
            <i className="ri-newspaper-fill text-xl"></i>
          </span>
          <span className="leading-none">
            <span className="font-heading text-2xl font-black tracking-tight md:text-3xl">
              {siteName}
            </span>
            <span className="mt-1 block font-label text-[11px] uppercase tracking-[0.2em] text-foreground-500">
              Chigua Toutiao
            </span>
          </span>
        </a>

        <p className="hidden max-w-md text-sm leading-relaxed text-foreground-500 lg:block">
          第一时间为你带来最新独家猛料与深度深扒，内幕不设限，满足你对真相的全部好奇。
        </p>

        <form onSubmit={submit} className="flex w-full items-center md:w-auto">
          <div className="flex w-full items-center overflow-hidden rounded-full border border-background-300 bg-background-50 focus-within:border-primary-400 md:w-64">
            <i className="ri-search-line pl-4 text-base text-foreground-400"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索你关心的瓜…"
              aria-label="搜索"
              className="w-full bg-transparent px-3 py-2.5 text-sm outline-none placeholder:text-foreground-400"
            />
            {keyword && (
              <button
                type="button"
                aria-label="清空搜索"
                onClick={() => setKeyword("")}
                className="pr-2 text-foreground-400 hover:text-primary-500"
              >
                <i className="ri-close-circle-fill"></i>
              </button>
            )}
          </div>
          <button
            type="submit"
            className="ml-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 font-label text-sm font-bold text-background-50 transition hover:bg-primary-600"
          >
            搜索
          </button>
        </form>
      </div>

      {/* 吸顶栏目导航 */}
      <nav
        className={`sticky top-0 z-40 border-y border-background-200 transition-all duration-300 ${
          scrolled ? "bg-background-50/95 shadow-sm backdrop-blur" : "bg-background-100"
        }`}
        aria-label="栏目导航"
      >
        <div className="mx-auto flex max-w-7xl items-center gap-2 px-4 py-2.5 md:px-6">
          <button
            type="button"
            onClick={() => jumpAnchor("feed")}
            className="hidden shrink-0 items-center gap-1.5 rounded-full bg-foreground-950 px-4 py-1.5 font-label text-sm font-bold text-background-50 transition hover:bg-foreground-800 md:inline-flex"
          >
            <i className="ri-fire-fill text-accent-400"></i>
            热点资讯
          </button>
          <div className="editor-scroll hidden flex-1 items-center gap-1 overflow-x-auto md:flex">
            {categoryNav.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => goCategory(c.id)}
                className="whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium text-foreground-700 transition hover:bg-background-50 hover:text-primary-600"
              >
                <i className={`mr-1.5 ${c.icon} text-foreground-400`}></i>
                {c.name}
              </button>
            ))}
          </div>
          <span className="hidden items-center gap-1.5 text-xs text-foreground-500 lg:inline-flex">
            <i className="ri-checkbox-circle-line text-accent-600"></i>
            已更新 09:58
          </span>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="打开栏目菜单"
            className="ml-auto inline-flex h-9 w-9 items-center justify-center rounded-full border border-background-300 bg-background-50 text-xl text-foreground-800 transition hover:text-primary-600 md:hidden"
          >
            <i className={menuOpen ? "ri-close-line" : "ri-menu-3-line"}></i>
          </button>
        </div>

        {/* 移动端菜单 */}
        {menuOpen && (
          <div className="border-t border-background-200 bg-background-50 px-4 pb-4 md:hidden">
            <p className="pt-3 font-label text-xs font-bold uppercase tracking-widest text-foreground-400">
              栏目直达
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {categoryNav.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => goCategory(c.id)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-4 py-2 text-sm font-medium text-secondary-900 transition hover:bg-primary-500 hover:text-background-50"
                >
                  <i className={`${c.icon} text-base`}></i>
                  {c.name}
                </button>
              ))}
            </div>
            <p className="pt-4 font-label text-xs font-bold uppercase tracking-widest text-foreground-400">
              页内直达
            </p>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {quickAnchors.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => jumpAnchor(a.id)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-background-200 px-3 py-2 text-sm text-foreground-700 transition hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-arrow-right-up-line text-foreground-400"></i>
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}